import { createContext, useCallback, useContext, useRef, useState } from "react";
import type { ReactNode } from "react";

type PendingNav = (() => void) | null;

type NavigationGuardContextValue = {
  setDirty: (dirty: boolean) => void;
  isDirty: () => boolean;
  guardedNavigate: (go: () => void) => void;
  pendingNav: PendingNav;
  confirmNav: () => void;
  cancelNav: () => void;
};

const NavigationGuardContext = createContext<NavigationGuardContextValue | null>(null);

export function NavigationGuardProvider({ children }: { children: ReactNode }) {
  const dirtyRef = useRef(false);
  const [pendingNav, setPendingNav] = useState<PendingNav>(null);

  const setDirty = useCallback((dirty: boolean) => {
    dirtyRef.current = dirty;
  }, []);

  const isDirty = useCallback(() => dirtyRef.current, []);

  const guardedNavigate = useCallback((go: () => void) => {
    if (dirtyRef.current) {
      setPendingNav(() => go);
    } else {
      go();
    }
  }, []);

  const confirmNav = useCallback(() => {
    const go = pendingNav;
    setPendingNav(null);
    dirtyRef.current = false;
    go?.();
  }, [pendingNav]);

  const cancelNav = useCallback(() => {
    setPendingNav(null);
  }, []);

  return (
    <NavigationGuardContext.Provider
      value={{ setDirty, isDirty, guardedNavigate, pendingNav, confirmNav, cancelNav }}
    >
      {children}
    </NavigationGuardContext.Provider>
  );
}

export function useNavigationGuard() {
  const ctx = useContext(NavigationGuardContext);
  if (!ctx) throw new Error("useNavigationGuard must be used within NavigationGuardProvider");
  return ctx;
}
